import authRequest from './authRequest';
import Bugsnag from "@bugsnag/js";

// 28 seconds timeout, a bit less than Heroku's default 30 seconds timeout threshold
const CLIENT_TIMEOUT = 28000;

const sendAudioData = async (chunkData, authToken = null) => {
    const formData = new FormData();
    formData.append('audio', chunkData.blob);
    formData.append('index', chunkData.chunkIndex);

    let timeoutId;
    let controller;
    const startedAt = Date.now();
    const label = `[upload s=${chunkData.sessionUuid} c=${chunkData.chunkIndex}]`;
    const isProd = process.env.NODE_ENV === 'production';

    try {
        controller = new AbortController();
        timeoutId = setTimeout(() => {
            console.warn(`${label} timeout after ${CLIENT_TIMEOUT}ms`);
            controller.abort();
        }, CLIENT_TIMEOUT);

        if (isProd) {
            try {
                // Optional detailed breadcrumb for correlating in Bugsnag
                Bugsnag.leaveBreadcrumb('audioUpload:start', {
                    sessionUuid: chunkData.sessionUuid,
                    chunkIndex: String(chunkData.chunkIndex),
                    blobSize: String(chunkData?.blob?.size || 0),
                    blobType: String(chunkData?.blob?.type || ''),
                    timeoutMs: String(CLIENT_TIMEOUT),
                });
            } catch (_) {}
        }
        console.log(`${label} starting; size=${chunkData?.blob?.size || 0}B type=${chunkData?.blob?.type || ''}`);

        const response = await authRequest(`/sessions/${chunkData.sessionUuid}/upload_partial`, {
            method: 'POST',
            body: formData,
            signal: controller.signal,
        }, authToken);
        const elapsedMs = Date.now() - startedAt;
        const reqId = (response && response.headers && typeof response.headers.get === 'function') ? (response.headers.get('x-request-id') || response.headers.get('X-Request-Id')) : null;
        console.log(`${label} response status=${response.status} ok=${response.ok} in ${elapsedMs}ms${reqId ? ` reqId=${reqId}` : ''}`);
        if (isProd) {
            try {
                Bugsnag.leaveBreadcrumb('audioUpload:response', {
                    sessionUuid: chunkData.sessionUuid,
                    chunkIndex: String(chunkData.chunkIndex),
                    status: String(response.status),
                    ok: String(response.ok),
                    elapsedMs: String(elapsedMs),
                    requestId: reqId || '',
                });
            } catch (_) {}
        }

        if (response.status === 200) {
            return true;
        } else {
            if (isProd) {
                try { Bugsnag.notify(new Error(`Error uploading audio chunk: ${JSON.stringify(response)}`)) } catch (_) {}
            }
            console.error(`${label} failed: ${response.status} ${response.statusText}`);
            return false;
        }
    } catch (err) {
        // Handle specific error types
        if (err.name === 'AbortError') {
            console.warn(`${label} aborted by client (timeout)`);
            // Don't report AbortError to Bugsnag as it's expected behavior
            return false;
        } else if (err.isAxiosError && err.response && err.response.status === 422) {
            // Treat 422 Unprocessable (duplicate partial index) as already uploaded
            console.warn(`${label} server returned 422 (possibly duplicate). Treating as already uploaded.`);
            return true;
        } else {
            console.error(`${label} error:`, err);
            if (isProd) {
                try { Bugsnag.notify(err); } catch (_) {}
            }
            return false;
        }
    } finally {
        // Clear timeout if it hasn't fired yet
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        const elapsedMs = Date.now() - startedAt;
        if (isProd) {
            try {
                Bugsnag.leaveBreadcrumb('audioUpload:finish', {
                    sessionUuid: chunkData.sessionUuid,
                    chunkIndex: String(chunkData.chunkIndex),
                    elapsedMs: String(elapsedMs),
                });
            } catch (_) {}
        }
    }
};

export default sendAudioData;
